import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Button,
  Grid,
  Stack,
  Box,
  TextField,
  MenuItem,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";

import { PageHeader, SubtitleM, SubtitleS } from "../../components/Typography";
import useApiCall from "../../hooks/useApiCall";

// Helper function
const getAgentStatuses = () => ["draft", "active", "inactive", "archived"];

export default function AgentInstancesList() {
  const navigate = useNavigate();
  const location = useLocation();
  const apps = useSelector((state) => state?.apps.list || []);
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();

  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncingId, setSyncingId] = useState(null);

  // Parse query params for pre-filtering
  const queryParams = new URLSearchParams(location.search);
  const initialTemplateId = queryParams.get("template_id") || "";

  const [filters, setFilters] = useState({
    app_id: "",
    site_code: "",
    template_id: initialTemplateId,
    status: "",
    search: "",
  });

  // Fetch instances on mount and when filters change
  useEffect(() => {
    fetchInstances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchInstances = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (filters.app_id) queryParams.append('app_id', filters.app_id);
      if (filters.site_code) queryParams.append('site_code', filters.site_code);
      if (filters.template_id) queryParams.append('template_id', filters.template_id);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.search) queryParams.append('search', filters.search);

      const queryString = queryParams.toString();
      const url = `/v2/agent_instances${queryString ? `?${queryString}` : ''}`;

      const response = await makeRequest(url, {
        method: 'GET',
        authTokens: tokens,
      });
      setInstances(response.instances || []);
    } catch (error) {
      console.error("Error fetching instances:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (instance, event) => {
    event.stopPropagation();

    if (!instance.template_id) {
      alert("This instance is not linked to a template");
      return;
    }

    if (
      window.confirm(
        `Sync "${instance.name}" from its template?\n\nThis will update the instance with the latest template changes while preserving your configuration overrides.`
      )
    ) {
      setSyncingId(instance.id);
      try {
        await makeRequest(`/v2/agent_instances/${instance.id}/sync`, {
          method: 'POST',
          authTokens: tokens,
        });
        alert("Instance synced successfully!");
        fetchInstances(); // Refresh the list
      } catch (error) {
        console.error("Error syncing instance:", error);
        alert(`Failed to sync: ${error.response?.data?.error || error.message}`);
      } finally {
        setSyncingId(null);
      }
    }
  };

  const handleInstanceClick = (instance) => {
    navigate(`/agent-instances/${instance.id}`);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getClientName = (appId) => {
    const app = apps.find((a) => a.id === appId);
    return app ? app.client_name : `App ID: ${appId}`;
  };

  const hasOverrides = (instance) => {
    return (
      instance.configuration_overrides &&
      Object.keys(instance.configuration_overrides).length > 0
    );
  };

  return (
    <>
      <Stack
        sx={{ width: "100%", margin: "10px" }}
        flexDirection={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <PageHeader>Agent Instances</PageHeader>
        <Button
          title="View Templates"
          variant="PrimaryButton"
          onClick={() => navigate("/agent-templates")}
        >
          View Templates
        </Button>
      </Stack>

      {/* Filters */}
      <Stack
        direction="row"
        spacing={2}
        sx={{ margin: "10px", marginBottom: "20px", flexWrap: "wrap" }}
      >
        <TextField
          label="Search"
          variant="outlined"
          size="small"
          value={filters.search}
          onChange={(e) => handleFilterChange("search", e.target.value)}
          sx={{ minWidth: "200px" }}
        />
        <TextField
          select
          label="Client"
          variant="outlined"
          size="small"
          value={filters.app_id}
          onChange={(e) => handleFilterChange("app_id", e.target.value)}
          sx={{ minWidth: "200px" }}
        >
          <MenuItem value="">All Clients</MenuItem>
          {apps.map((app) => (
            <MenuItem key={app.id} value={app.id}>
              {app.client_name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Site Code"
          variant="outlined"
          size="small"
          value={filters.site_code}
          onChange={(e) => handleFilterChange("site_code", e.target.value)}
          sx={{ minWidth: "150px" }}
        />
        <TextField
          select
          label="Status"
          variant="outlined"
          size="small"
          value={filters.status}
          onChange={(e) => handleFilterChange("status", e.target.value)}
          sx={{ minWidth: "150px" }}
        >
          <MenuItem value="">All</MenuItem>
          {getAgentStatuses().map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Instances Grid */}
      {!loading && (
        <Grid
          sx={{
            rowGap: "26px",
            columnGap: "26px",
            paddingBottom: "10px",
            margin: "10px",
          }}
          container
        >
          {instances.map((instance) => (
            <Grid
              item
              key={instance.id}
              component={Box}
              lg={3}
              sx={{
                border: "1px solid #D8DCE2",
                borderRadius: "10px",
                minHeight: "180px",
                padding: "16px",
                minWidth: "364px !important",
                maxWidth: "364px !important",
                cursor: "pointer",
                "&:hover": {
                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                },
              }}
              onClick={() => handleInstanceClick(instance)}
            >
              <Stack
                direction="column"
                spacing={1}
                sx={{ height: "100%", justifyContent: "space-between" }}
              >
                {/* Instance Name */}
                <Stack direction="row" spacing={1} alignItems="center">
                  <SubtitleM
                    style={{
                      wordBreak: "break-word",
                      color: "#000000",
                      textAlign: "left",
                      fontWeight: "bold",
                      flex: 1,
                    }}
                  >
                    {instance.name}
                  </SubtitleM>
                  {hasOverrides(instance) && (
                    <Chip
                      label="Custom"
                      size="small"
                      color="primary"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  )}
                </Stack>

                {/* Instance Details */}
                <Stack direction="column" spacing={0.5}>
                  <SubtitleS style={{ color: "#666" }}>
                    Client: {getClientName(instance.app_id)}
                  </SubtitleS>
                  <SubtitleS style={{ color: "#666" }}>
                    Site: {instance.site_code}
                  </SubtitleS>
                  <SubtitleS style={{ color: "#666" }}>
                    Status: {instance.status || "N/A"}
                  </SubtitleS>
                  {instance.template_id && (
                    <SubtitleS
                      style={{
                        color: "#1976d2",
                        marginTop: "8px",
                      }}
                    >
                      Template:{" "}
                      <Link
                        to={`/agent-templates/${instance.template_id}/edit`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ textDecoration: "underline" }}
                      >
                        View Template
                      </Link>
                    </SubtitleS>
                  )}
                </Stack>

                {/* Action Buttons */}
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ justifyContent: "flex-end" }}
                >
                  <IconButton
                    size="small"
                    title="View Details"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/agent-instances/${instance.id}`);
                    }}
                  >
                    <ViewIcon fontSize="small" />
                  </IconButton>
                  {instance.template_id && (
                    <IconButton
                      size="small"
                      title="Sync from Template"
                      onClick={(e) => handleSync(instance, e)}
                      disabled={syncingId === instance.id}
                      color="primary"
                    >
                      {syncingId === instance.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SyncIcon fontSize="small" />
                      )}
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            </Grid>
          ))}

          {/* Empty State */}
          {instances.length === 0 && (
            <Box sx={{ width: "100%", textAlign: "center", my: 4 }}>
              <SubtitleM>No instances found</SubtitleM>
              <SubtitleS style={{ marginTop: "8px", color: "#666" }}>
                Clone a template to create agent instances for your clients
              </SubtitleS>
              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={() => navigate("/agent-templates")}
              >
                View Templates
              </Button>
            </Box>
          )}
        </Grid>
      )}
    </>
  );
}
